/**
 * `/workflows/monitoring/:templateId` — one fund's monitoring workflow.
 *
 * The same four tabs every workflow has (DOP-478): what it produces (Data),
 * what is blocking the period (Needs review), what has been generated
 * (History), how the template is configured (Settings). Only the Data body is
 * monitoring-shaped — Metrics, Periods and Borrowers, the three views this page
 * had as top-level tabs before the unified IA.
 *
 * It opens on **Periods** (operator's call, 2026-09-08). The board is what
 * someone opens a monitoring workflow to look at — who has sent their package
 * and what has been run this quarter — and Metrics is the recipe behind it,
 * read once when the template is set up and rarely after. A brand-new template
 * has no periods, and the board's own empty state — the form that adds the
 * first one — is the first thing that workflow should say anyway.
 *
 * Those three now live in `?view=`, never in `?tab=` (epic decision 6): the
 * shell owns `?tab=` and a page that borrowed it would make "Periods" mean two
 * different things one click apart. `?period=` is untouched, and every link
 * written before this ticket — including the `?tab=recipe` ones DOP-458 left
 * behind — is rewritten on load rather than dropped, because a period board is
 * the thing an analyst pastes into Slack on deadline week.
 *
 * Every write goes through the toast rather than an inline banner: a run
 * request answers with counts (queued / skipped) that are a notice, not a
 * region of the page, and the board underneath re-renders with the truth a
 * moment later anyway.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import DashboardShell, {
	SHELL_TAB_PARAM,
} from "../../components/Dashboards/DashboardShell";
import ErrorDisplay from "../../components/ErrorDisplay";
import BorrowerList from "../../components/Monitoring/BorrowerList";
import NeedsReviewList, {
	reviewCount,
} from "../../components/Monitoring/NeedsReviewList";
import PeriodBoard from "../../components/Monitoring/PeriodBoard";
import PeriodStrip from "../../components/Monitoring/PeriodStrip";
import MetricsView from "../../components/Monitoring/MetricsView";
import MonitoringTour from "../../components/Monitoring/MonitoringTour";
import NewPeriodForm from "../../components/Monitoring/NewPeriodForm";
import RunHistoryList from "../../components/Monitoring/RunHistoryList";
import TemplateSettingsView from "../../components/Monitoring/TemplateSettingsView";
import TakeTourButton from "../../components/Tour/TakeTourButton";
import { useToast } from "../../context/ToastContext";
import { useMonitoringBoard } from "../../hooks/useMonitoringBoard";
import { useMonitoringTemplate } from "../../hooks/useMonitoringTemplate";
import {
	MONITORING_TOUR_SEEN_KEY,
	useWorkflowTour,
} from "../../hooks/useWorkflowTour";
import {
	MonitoringConflictError,
	monitoringService,
} from "../../services/monitoringService";
import type {
	MonitoringBoard,
	MonitoringCreateBorrowerPayload,
	MonitoringMissingDataPolicy,
	MonitoringPeriod,
} from "../../types/monitoring";
import { friendlyErrorMessage } from "../../utils/apiErrorDetail";

const VIEW_PARAM = "view";
const PERIOD_PARAM = "period";

/**
 * Where a link written before DOP-625 lands now. The page had four shell tabs
 * (Data, Needs review, History, Settings) with Metrics / Periods / Borrowers
 * under Data in `?view=`, and before that (DOP-480) those three were tabs of
 * their own — `recipe` being Metrics' id until DOP-458. Every one of them is
 * in links people have sent each other, so each is rewritten, not dropped.
 */
function legacyTab(tab: string | null, view: string | null): string | null {
	if (tab === "data" || tab === null) {
		if (view === "metrics" || view === "recipe") return "setup";
		if (view === "borrowers") return "borrowers";
		if (view === "periods") return "quarter";
		return tab === "data" ? "quarter" : null;
	}
	if (tab === "settings" || tab === "metrics" || tab === "recipe") return "setup";
	if (tab === "periods") return "quarter";
	return null;
}

/** "This quarter" for a quarterly template, "This month" for a monthly one. */
const currentTabLabel = (cadence: string | null | undefined): string => {
	const value = (cadence ?? "").toLowerCase();
	if (value.startsWith("quarter")) return "This quarter";
	if (value.startsWith("month")) return "This month";
	if (value.startsWith("annual") || value.startsWith("year")) return "This year";
	return "This period";
};

/**
 * The message a failed write should show.
 *
 * A 409 is shown VERBATIM: the server wrote it about this user's own input
 * ("A period ending 2025-12-31 already exists"), it names the thing to change,
 * and paraphrasing it would lose the date. Everything else goes through the
 * deny-list, which swaps status lines and stack text for the fallback.
 *
 * Same rule as the run page's `messageOf`; the two surfaces must not disagree
 * about what a conflict looks like.
 */
const writeErrorMessage = (caught: unknown, fallback: string): string =>
	caught instanceof MonitoringConflictError
		? caught.message
		: friendlyErrorMessage(caught, fallback);

/** The skeleton `LoadingState` shows for a grid-shaped surface. */
const Skeleton: React.FC = () => (
	<div className="h-[420px] animate-pulse rounded-lg border border-gray-200 bg-white" />
);

const MonitoringTemplatePage: React.FC = () => {
	const { templateId } = useParams<{ templateId: string }>();
	const [searchParams, setSearchParams] = useSearchParams();
	const { showToast } = useToast();

	const {
		template,
		borrowers,
		borrowerCandidates,
		periods,
		loading,
		error,
		refresh,
		addBorrower,
		addPeriod,
		updateTemplate,
	} = useMonitoringTemplate(templateId);

	// The period the URL names, falling back to the newest one the template has
	// — periods come back oldest-first, so the last is the quarter in progress,
	// which is the one someone opening this page is working on.
	const periodParam = searchParams.get(PERIOD_PARAM);
	const selectedPeriod: MonitoringPeriod | null = useMemo(() => {
		if (periods.length === 0) return null;
		const named = periods.find((period) => period.public_id === periodParam);
		return named ?? periods[periods.length - 1];
	}, [periods, periodParam]);

	const {
		board,
		loading: boardLoading,
		stale,
		error: boardError,
		refresh: refreshBoard,
	} = useMonitoringBoard(selectedPeriod?.public_id);

	// The tour points at the quarter board, so it starts on its own only once
	// a board is on screen there.
	const onQuarterTab = (searchParams.get(SHELL_TAB_PARAM) ?? "quarter") === "quarter";
	const tour = useWorkflowTour(MONITORING_TOUR_SEEN_KEY, onQuarterTab && board !== null);

	const setParam = useCallback(
		(key: string, value: string) => {
			const next = new URLSearchParams(searchParams);
			next.set(key, value);
			setSearchParams(next);
		},
		[searchParams, setSearchParams],
	);

	/**
	 * Rewrite an older link into the params it would be written with today.
	 *
	 * Replaced, not pushed: the old shape must not sit in the history for Back
	 * to bounce off. It runs once per stale link, because the rewrite removes
	 * what triggers it.
	 */
	const rewriteTo = legacyTab(searchParams.get(SHELL_TAB_PARAM), searchParams.get(VIEW_PARAM));
	useEffect(() => {
		if (rewriteTo === null) return;
		const next = new URLSearchParams(searchParams);
		next.set(SHELL_TAB_PARAM, rewriteTo);
		next.delete(VIEW_PARAM);
		setSearchParams(next, { replace: true });
	}, [rewriteTo, searchParams, setSearchParams]);

	// A period created from the strip becomes the selected one straight away:
	// the reason to add a quarter is to work in it.
	const [pendingPeriodId, setPendingPeriodId] = useState<string | null>(null);
	useEffect(() => {
		if (!pendingPeriodId) return;
		setParam(PERIOD_PARAM, pendingPeriodId);
		setPendingPeriodId(null);
	}, [pendingPeriodId, setParam]);

	/** Periods where the board says every borrower is locked, for the strip's lock. */
	const lockedPeriodIds = useMemo(() => {
		const locked = new Set<string>();
		if (
			board &&
			board.counts.borrowers > 0 &&
			board.counts.locked === board.counts.borrowers
		) {
			locked.add(board.period.public_id);
		}
		return locked;
	}, [board]);

	const nextPeriod = useMemo(() => {
		if (!selectedPeriod) return null;
		const index = periods.findIndex(
			(period) => period.public_id === selectedPeriod.public_id,
		);
		const later = index >= 0 ? periods[index + 1] : undefined;
		return later
			? { label: later.label, package_due: later.package_due }
			: null;
	}, [periods, selectedPeriod]);

	const [runningPeriod, setRunningPeriod] = useState(false);
	const [runningBorrowerIds, setRunningBorrowerIds] = useState<ReadonlySet<string>>(
		() => new Set<string>(),
	);

	const announceRun = useCallback(
		(queued: number, skipped: number) => {
			if (queued === 0) {
				showToast(
					skipped > 0
						? `Nothing to run — ${skipped} skipped.`
						: "Nothing to run.",
					"info",
				);
				return;
			}
			showToast(
				skipped > 0
					? `Queued ${queued}, skipped ${skipped}.`
					: `Queued ${queued}.`,
				"success",
			);
		},
		[showToast],
	);

	const runPeriod = useCallback(async () => {
		if (!selectedPeriod) return;
		setRunningPeriod(true);
		try {
			const result = await monitoringService.runPeriod(selectedPeriod.public_id, {
				// Only borrowers whose package is in: queuing a run against nothing
				// burns a model call to produce a ledger of gaps.
				only_received: true,
			});
			announceRun(result.queued.length, result.skipped.length);
			refreshBoard();
		} catch (caught) {
			showToast(
				writeErrorMessage(caught, "Could not start the runs."),
				"error",
			);
		} finally {
			setRunningPeriod(false);
		}
	}, [selectedPeriod, announceRun, refreshBoard, showToast]);

	const runBorrower = useCallback(
		async (borrowerId: string) => {
			if (!selectedPeriod) return;
			setRunningBorrowerIds((current) => new Set(current).add(borrowerId));
			try {
				const result = await monitoringService.runPeriod(
					selectedPeriod.public_id,
					{ borrower_ids: [borrowerId] },
				);
				announceRun(result.queued.length, result.skipped.length);
				refreshBoard();
			} catch (caught) {
				showToast(
					writeErrorMessage(caught, "Could not start the run."),
					"error",
				);
			} finally {
				setRunningBorrowerIds((current) => {
					const next = new Set(current);
					next.delete(borrowerId);
					return next;
				});
			}
		},
		[selectedPeriod, announceRun, refreshBoard, showToast],
	);

	const exportPeriod = useCallback(async () => {
		if (!selectedPeriod) return;
		try {
			monitoringService.saveExportFile(
				await monitoringService.exportPeriod(selectedPeriod.public_id),
			);
		} catch (caught) {
			showToast(
				writeErrorMessage(caught, "Could not export the period pack."),
				"error",
			);
		}
	}, [selectedPeriod, showToast]);

	/**
	 * One of the two editable parts of the template (DOP-456, DOP-462).
	 *
	 * Saved on the click with no Save button: it is one setting of two, and the
	 * radio's own state is the confirmation. Because the hook replaces the
	 * template only on success, a refused save leaves the radio showing the
	 * policy that is actually stored, and the toast says why.
	 */
	const [savingPolicy, setSavingPolicy] = useState(false);
	const changePolicy = useCallback(
		async (policy: MonitoringMissingDataPolicy) => {
			setSavingPolicy(true);
			try {
				await updateTemplate({ missing_data_policy: policy });
			} catch (caught) {
				showToast(
					writeErrorMessage(caught, "Could not save the policy."),
					"error",
				);
			} finally {
				setSavingPolicy(false);
			}
		},
		[updateTemplate, showToast],
	);

	/**
	 * Include or exclude one metric (DOP-462).
	 *
	 * The PATCH carries a map of exactly one key, so two quick clicks on two
	 * different rows cannot undo each other, and a row the server changed
	 * meanwhile is left alone. In-flight keys are tracked per row rather than
	 * with one page-wide flag: freezing the whole list because one checkbox is
	 * saving would read as a page that stopped working.
	 *
	 * Like the policy, nothing is copied locally — the hook swaps in the stored
	 * template on success, so a refused save leaves the box where it was and the
	 * toast says why.
	 */
	const [savingFigureKeys, setSavingFigureKeys] = useState<ReadonlySet<string>>(
		() => new Set<string>(),
	);
	const toggleFigure = useCallback(
		async (key: string, enabled: boolean) => {
			setSavingFigureKeys((current) => new Set(current).add(key));
			try {
				await updateTemplate({ figure_enabled: { [key]: enabled } });
			} catch (caught) {
				showToast(
					writeErrorMessage(caught, "Could not save the metric."),
					"error",
				);
			} finally {
				setSavingFigureKeys((current) => {
					const next = new Set(current);
					next.delete(key);
					return next;
				});
			}
		},
		[updateTemplate, showToast],
	);

	/**
	 * The forms' contract: these NEVER reject.
	 *
	 * They used to hand the hook's promise straight to the form, so a 409 from
	 * `createPeriod` ("A period ending 2025-12-31 already exists") escaped as an
	 * unhandled `MonitoringConflictError` and the dev server put a red overlay
	 * over the page. A write that the server correctly refused is not a crash —
	 * it is an answer, and the form's job is to show it and keep what was typed.
	 */
	const createPeriod = useCallback(
		async (payload: { label: string; period_end: string }) => {
			try {
				const created = await addPeriod(payload);
				setPendingPeriodId(created.public_id);
				return { ok: true };
			} catch (caught) {
				const message = writeErrorMessage(
					caught,
					"Could not add the period.",
				);
				const conflict = caught instanceof MonitoringConflictError;
				// A conflict is about the date the user just chose, so the form
				// says so under that field — and only there. Anything else is
				// not the user's input, so it goes to the toast instead.
				if (!conflict) {
					showToast(message, "error");
				}
				return { ok: false, conflict, message };
			}
		},
		[addPeriod, showToast],
	);

	const createBorrower = useCallback(
		async (payload: MonitoringCreateBorrowerPayload) => {
			try {
				await addBorrower(payload);
				return { ok: true };
			} catch (caught) {
				showToast(
					writeErrorMessage(caught, "Could not add the borrower."),
					"error",
				);
				return { ok: false };
			}
		},
		[addBorrower, showToast],
	);

	if (loading && template === null) {
		return (
			<div className="h-full overflow-y-auto bg-gray-50 px-6 py-6">
				<Skeleton />
			</div>
		);
	}

	if (error && template === null) {
		return (
			<div className="h-full overflow-y-auto bg-gray-50 py-6">
				<ErrorDisplay error={error} onRetry={refresh} />
			</div>
		);
	}

	if (!template) return null;

	const periodStrip = (
		<PeriodStrip
			periods={periods}
			selectedId={selectedPeriod?.public_id ?? null}
			lockedIds={lockedPeriodIds}
			onSelect={(periodId) => setParam(PERIOD_PARAM, periodId)}
			onCreate={createPeriod}
		/>
	);

	/**
	 * The three surfaces that read the selected period's board, sharing one set
	 * of empty / error / loading answers — the board is one fetch, so a tab that
	 * told a different story about it would be telling a story about itself.
	 */
	const withBoard = (render: (loaded: MonitoringBoard) => React.ReactNode) => {
		if (periods.length === 0) {
			// The first period is the first thing a new workflow needs, so the
			// form is on the page rather than behind a menu.
			return (
				<section
					aria-labelledby="first-period-title"
					className="rounded-xl border border-gray-200 bg-white px-6 py-6"
				>
					<h2 id="first-period-title" className="text-[15px] font-semibold text-[#111111]">
						Add your first period
					</h2>
					<p className="mt-1 text-[13px] text-[#5B6472]">
						No periods yet. A period is the quarter whose packages you collect and run — name it and give the date it ends.
					</p>
					<div className="mt-4">
						<NewPeriodForm onCreate={createPeriod} layout="row" />
					</div>
				</section>
			);
		}
		let body: React.ReactNode = null;
		if (boardError && board === null) {
			body = <ErrorDisplay error={boardError} onRetry={refreshBoard} />;
		} else if (boardLoading && board === null) {
			body = <Skeleton />;
		} else if (board) {
			body = render(board);
		}
		return (
			<>
				{periodStrip}
				{body}
			</>
		);
	};

	const meta = [
		template.fund_name,
		template.cadence ? template.cadence.charAt(0).toUpperCase() + template.cadence.slice(1) : null,
		`${template.borrowers_count} ${template.borrowers_count === 1 ? "borrower" : "borrowers"}`,
	]
		.filter(Boolean)
		.join(" · ");

	// The count is only claimed once the board has answered: a "Needs you 0"
	// badge on a period nobody has read yet would be a reassurance the page has
	// not earned.
	//
	// Until the first period exists the only thing to do is add it, on the
	// first tab; the rest stay visible so the shape of the workflow is clear,
	// but greyed out rather than each repeating the same "add a period" card.
	const noPeriod = periods.length === 0;
	const tabs = [
		{ id: "quarter", label: currentTabLabel(template.cadence) },
		{
			id: "review",
			label: "Needs you",
			disabled: noPeriod,
			...(board ? { count: reviewCount(board.rows, board.period), countTone: "attention" as const } : {}),
		},
		{ id: "borrowers", label: "Borrowers", disabled: noPeriod },
		{ id: "history", label: "History", disabled: noPeriod },
		{ id: "setup", label: "How it's set up", disabled: noPeriod },
	];

	const showTab = (tab: string) => setParam(SHELL_TAB_PARAM, tab);

	return (
		<DashboardShell
			title={template.name}
			meta={meta}
			tabs={tabs}
			actions={
				<>
					{periods.length > 0 && (
						<TakeTourButton
							onClick={() => {
								showTab("quarter");
								tour.start();
							}}
						/>
					)}
					{selectedPeriod && (
						<>
							<button
								type="button"
								onClick={() => void exportPeriod()}
								// The workbook the whole period exports to; a per-run one
								// lives on the run page.
								aria-label={`Download the ${selectedPeriod.label} workbook`}
								className="inline-flex h-9 items-center whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3.5 text-sm font-medium text-[#1F2937] hover:bg-gray-50"
							>
								Download workbook
							</button>
							<button
								type="button"
								onClick={() => void runPeriod()}
								disabled={runningPeriod}
								// Only packages that are in are run (`only_received`); the
								// period is in the accessible name for out-of-context readers.
								aria-label={`Run the received packages for ${selectedPeriod.label}`}
								data-tour="run-received"
								className="inline-flex h-9 items-center whitespace-nowrap rounded-lg bg-[#004C4B] px-3.5 text-sm font-semibold text-white hover:bg-[#003a3a] disabled:opacity-60"
							>
								{runningPeriod ? "Starting…" : "Run received packages"}
							</button>
						</>
					)}
				</>
			}
		>
			{(tab) => {
				if (tab === "setup") {
					return (
						<div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
							<MetricsView
								template={template}
								onToggleFigure={toggleFigure}
								savingFigureKeys={savingFigureKeys}
							/>
							<TemplateSettingsView
								template={template}
								onChangePolicy={changePolicy}
								savingPolicy={savingPolicy}
							/>
						</div>
					);
				}

				if (tab === "borrowers") {
					return (
						<BorrowerList
							borrowers={borrowers}
							candidates={borrowerCandidates}
							onCreate={createBorrower}
						/>
					);
				}

				if (tab === "review") {
					return withBoard((loaded) => (
						<NeedsReviewList
							rows={loaded.rows}
							period={loaded.period}
							onRunBorrower={(borrowerId) => void runBorrower(borrowerId)}
							runningBorrowerIds={runningBorrowerIds}
						/>
					));
				}

				if (tab === "history") {
					return withBoard((loaded) => (
						<RunHistoryList rows={loaded.rows} periodLabel={loaded.period.label} />
					));
				}

				return withBoard((loaded) => (
					<>
					{tour.open && <MonitoringTour onClose={tour.end} />}
					<PeriodBoard
						board={loaded}
						stale={stale}
						onRunBorrower={(borrowerId) => void runBorrower(borrowerId)}
						runningBorrowerIds={runningBorrowerIds}
						runAllStarting={runningPeriod}
						nextPeriod={nextPeriod}
						borrowerCandidates={borrowerCandidates}
						onCreateBorrower={createBorrower}
						// The roster changed, so the board owes a new row.
						onBorrowerAdded={refreshBoard}
						onShowNeedsYou={() => showTab("review")}
					/>
					</>
				));
			}}
		</DashboardShell>
	);
};

export default MonitoringTemplatePage;
